/* eslint-disable web/complete-jsdoc-v2 -- Module-level JSDoc above documents the default export. */

/**
 * ContactPage — Phase 28 G6 (2026-05-19) factory-wired.
 *
 * Composed entirely of `@puffin-core/marketing-shell` primitives:
 * `<MarketingSeo>`, `<ContactForm>`, `<ContactFormSuccess>` + the
 * `createUseContactForm` hook. Mirror of port-web's Phase O.1
 * reference implementation, swapped for purchase-specific i18n bundle,
 * APP_KEY, brand, and the `interest` select field that purchase-api's
 * createContactSchema accepts.
 *
 * The local `useContactForm.js` hook + the inline `HoneypotField` /
 * `CSRFField` / `useRateLimit` security primitives have been retired —
 * the factory handles honeypot + rate-limit (server-side) + Turnstile
 * uniformly across all 7 consumers.
 *
 * Server contract: POSTs to `/api/v1/marketing/contact` (the purchase-api
 * `@puffin-core/marketing-contact-routes` consumer mount).
 *
 * @module portals/marketing-website/pages/ContactPage
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBrand } from '@puffin-core/brand-assets';
import { CaptchaField } from '@puffin-core/captcha-ui';
import { isEmailAddress } from '@puffin-core/email-address';
import {
  MarketingSeo,
  ContactForm,
  ContactFormSuccess,
  createUseContactForm,
  createContactApiClient,
  // AAS-I18N-002 ruling 42 (2026-08-10): Phase 1A extracted this adapter to a purchase-web
  // module so this page and the storefront CMS block shared ONE implementation. It is now
  // shared across all NINE consumers instead of one app's two — and the promotion is what
  // let the per-EMAIL limiter's 429 branch be fixed once rather than nine times.
  buildContactOutcomeAdapter,
  useAnalytics,
  organization,
  webPage,
} from '@puffin-core/marketing-shell';
import { IS_CAPTCHA_REQUIRED } from '../config/turnstileConfig';
import { useCaptchaFieldSync } from '../hooks/useCaptchaFieldSync';
import ContactFormHeader from '../components/ContactFormHeader';
import ContactInfoPanel from '../components/ContactInfoPanel';
import ContactInfoItem from '../components/ContactInfoItem';
import { PURCHASE_MARKETING_ROUTES } from '../constants/purchaseMarketingRoutePaths';

// MARKETING-CONTACT-CLIENT-001 (Wave 6.1): consume canonical
// `createContactApiClient` factory. purchase-api mounts the contact router
// at `/v1/marketing/contact`, the ONE fleet-wide mount (F-CONTACT-1); the
// `/api` prefix is stripped before routing.
const CONTACT_API_PATH = '/api/v1/marketing/contact';

const APP_KEY = 'purchase';
const CONTACT_TITLE = 'Contact Us - PurchasePuffin | Custom E-Commerce Platform';
const CONTACT_DESCRIPTION =
  'Get in touch with PurchasePuffin. Schedule a demo, get pricing, or discuss your custom storefront.';
const CONTACT_CANONICAL = 'https://purchasepuffin.com/contact';

const INTEREST_OPTIONS_DESC = Object.freeze([
  { value: 'schedule-demo', labelKey: 'contact.form.interestOptions.scheduleDemo' },
  { value: 'get-pricing', labelKey: 'contact.form.interestOptions.getPricing' },
  { value: 'general-inquiry', labelKey: 'contact.form.interestOptions.generalInquiry' },
  { value: 'partnership', labelKey: 'contact.form.interestOptions.partnership' },
]);

/**
 * Joi-shaped validator that returns the `{error, value}` contract
 * expected by `createUseContactForm`. Browser-bundle-friendly (no full
 * Joi runtime). Mirrors port-web's pattern + adds the `interest`
 * required-field rule.
 *
 * @param {(key:string) => string} t
 */
function buildContactValidator(t) {
  return {
    validate(values) {
      const details = [];
      const trimmed = {
        name: (values.name || '').trim(),
        email: (values.email || '').trim().toLowerCase(),
        company: (values.company || '').trim(),
        interest: (values.interest || '').trim(),
        message: (values.message || '').trim(),
        captchaToken: values.captchaToken || null,
        website: values.website || '',
      };
      if (!trimmed.name) {
        details.push({ path: ['name'], message: t('contact.validation.nameRequired') });
      } else if (trimmed.name.length > 200) {
        details.push({ path: ['name'], message: t('contact.validation.nameTooLong') });
      }
      if (!trimmed.email) {
        details.push({ path: ['email'], message: t('contact.validation.emailRequired') });
      } else if (!isEmailAddress(trimmed.email)) {
        details.push({ path: ['email'], message: t('contact.validation.emailInvalid') });
      }
      if (trimmed.company.length > 200) {
        details.push({ path: ['company'], message: t('contact.validation.companyTooLong') });
      }
      if (!trimmed.interest) {
        details.push({ path: ['interest'], message: t('contact.validation.interestRequired') });
      }
      if (trimmed.message.length > 5000) {
        details.push({ path: ['message'], message: t('contact.validation.messageTooLong') });
      }
      if (IS_CAPTCHA_REQUIRED && !trimmed.captchaToken) {
        details.push({
          path: ['captchaToken'],
          message: t('contact.validation.captchaRequired'),
        });
      }
      if (details.length > 0) {
        return { error: { details }, value: trimmed };
      }
      return { value: trimmed };
    },
  };
}

export default function ContactPage() {
  const [captchaToken, setCaptchaToken] = useState(null);

  // `getBrand` is a plain registry read (not a hook), so the jsonLd memo built
  // from it carries no hook dependency and therefore belongs ahead of the
  // custom hooks, per the canonical state → effect → computed → custom order.
  // It is still evaluated at RENDER time (not module load), so suites that stub
  // `@puffin-core/marketing-shell` keep resolving `organization` / `webPage`.
  const brand = getBrand(APP_KEY);

  const jsonLd = useMemo(
    () => [
      organization({
        appKey: APP_KEY,
        brand,
        description: CONTACT_DESCRIPTION,
      }),
      webPage({
        appKey: APP_KEY,
        brand,
        url: CONTACT_CANONICAL,
        title: CONTACT_TITLE,
        description: CONTACT_DESCRIPTION,
      }),
    ],
    [brand],
  );

  const { t } = useTranslation();
  const { trackPageView, trackEvent, track } = useAnalytics();

  useEffect(() => {
    trackPageView('/contact', 'Contact Page');
  }, [trackPageView]);

  const useHook = useMemo(
    () =>
      createUseContactForm({
        validator: buildContactValidator(t),
        apiClient: buildContactOutcomeAdapter(
          createContactApiClient({ endpoint: CONTACT_API_PATH }),
          CONTACT_API_PATH,
        ),
        t,
        defaultValues: {
          name: '',
          email: '',
          company: '',
          interest: '',
          message: '',
          website: '',
        },
        onFirstInteract: () => {
          if (typeof track === 'function') {
            track('contact_form_started', { component: 'ContactPage', appKey: APP_KEY });
          }
        },
        onOutcome: ({ outcome, submissionId }) => {
          if (typeof trackEvent === 'function') {
            trackEvent('contact', outcome, submissionId || undefined);
          }
          if (outcome === 'success' && typeof track === 'function') {
            track('contact_form_submitted', { submissionId: submissionId || null });
          }
        },
        i18nNamespacePrefix: 'contact',
      }),
    [t, trackEvent, track],
  );
  const form = useHook();

  // Bridge: the captcha token lives outside the form-state machine
  // (mounted via the captchaSlot prop). Sync it into form.fields so the
  // validator + submit envelope pick it up.
  useCaptchaFieldSync(form.setField, captchaToken);

  const interestOptions = useMemo(
    () =>
      INTEREST_OPTIONS_DESC.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
      })),
    [t],
  );

  const fields = useMemo(
    () => [
      {
        name: 'name',
        label: t('contact.form.name'),
        type: 'text',
        required: true,
        autoComplete: 'name',
        placeholder: t('contact.form.namePlaceholder'),
      },
      {
        name: 'email',
        label: t('contact.form.email'),
        type: 'email',
        required: true,
        autoComplete: 'email',
        placeholder: t('contact.form.emailPlaceholder'),
      },
      {
        name: 'company',
        label: t('contact.form.company'),
        type: 'text',
        autoComplete: 'organization',
        placeholder: t('contact.form.companyPlaceholder'),
      },
      {
        name: 'interest',
        label: t('contact.form.interest'),
        type: 'select',
        required: true,
        options: interestOptions,
      },
      {
        name: 'message',
        label: t('contact.form.message'),
        type: 'textarea',
        rows: 5,
        placeholder: t('contact.form.messagePlaceholder'),
      },
    ],
    [t, interestOptions],
  );

  const ogImageUrl = `https://purchasepuffin.com${brand.assets.ogCard}`;

  const successContent = (
    <ContactFormSuccess
      title={t('contact.success.title')}
      body={t('contact.success.message')}
      submissionId={form.submissionId || undefined}
      submissionIdLabel={t('contact.success.referenceLabel', { defaultValue: 'Reference ID' })}
      resetLabel={t('contact.success.resetButton')}
      onReset={form.reset}
      footerExtra={
        <Link to={PURCHASE_MARKETING_ROUTES.HOME} className="btn-secondary">
          {t('contact.success.homeLink')}
        </Link>
      }
    />
  );

  return (
    <section className="contact-page" aria-labelledby="contact-title">
      <MarketingSeo
        title={CONTACT_TITLE}
        description={CONTACT_DESCRIPTION}
        canonical={CONTACT_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />
      <div className="container">
        <div className="contact-grid">
          <div>
            <ContactFormHeader
              title={t('contact.form.title')}
              subtitle={t('contact.form.subtitle')}
            />

            <ContactForm
              form={form}
              fields={fields}
              submitLabel={t('contact.form.submit')}
              submittingLabel={t('contact.form.submitting')}
              formAriaLabel={t('contact.form.ariaLabel', { defaultValue: 'Contact form' })}
              captchaSlot={
                IS_CAPTCHA_REQUIRED ? (
                  <CaptchaField
                    onVerify={setCaptchaToken}
                    onError={() => setCaptchaToken(null)}
                    onExpire={() => setCaptchaToken(null)}
                  />
                ) : null
              }
              successContent={successContent}
            />
          </div>

          <ContactInfoPanel title={t('contact.info.title')}>
            <ContactInfoItem
              icon="✉️"
              label={t('contact.info.emailLabel')}
              value={t('contact.info.emailValue')}
            />

            <ContactInfoItem
              icon="🕐"
              label={t('contact.info.hoursLabel')}
              value={t('contact.info.hoursValue')}
            />

            <ContactInfoItem
              icon="📍"
              label={t('contact.info.locationLabel')}
              value={t('contact.info.locationValue')}
            />
          </ContactInfoPanel>
        </div>
      </div>
    </section>
  );
}
